import React, { useState, useEffect, useRef } from 'react';
import clsx from 'clsx';
import { useSelector, useDispatch } from 'react-redux';
import useScreenSize from 'hooks/useScreenSize';
import { getAIChat } from 'selectors/aiChat';
import { closeAIChat, clearActiveWorkflow, WorkflowTypes, toggleAIChat } from 'handlers/aiChat';
import Footer from 'components/Footer';
import ScrollToBottomButton from 'components/Common/ScrollToBottomButton';
import BackLink from 'components/BackLink/BackLink';

import { AI_COACH_DISCLAIMER } from 'components/Footer/Disclaimers/Disclaimers';
import Chat from 'components/FinancialCheckup/AICoach/Chat';
import PlaidWorkflow from 'components/FinancialCheckup/AICoach/Workflows/Plaid';
import ShiftImpactWorkflow from 'components/FinancialCheckup/AICoach/Workflows/ShiftImpact';
import ConversationWorkflow from 'components/FinancialCheckup/AICoach/Workflows/Conversation';
import { RoutePath } from 'enums/Routes';
import { useNavigate } from 'hooks/useNavigate';
import { FROM_FINANCIAL_DASHBOARD_STATE } from 'utils/navigationStates';

import { ReactComponent as CloseIcon } from 'images/cross-icon.svg';

import styles from './Wrapper.module.scss';

const MODAL_ANIMATION_DURATION = 400;

const WORKFLOWS_COMPONENTS = Object.freeze({
  [WorkflowTypes.Plaid]: <PlaidWorkflow />,
  [WorkflowTypes.ShiftImpact]: <ShiftImpactWorkflow />,
});

const Wrapper = () => {
  const dispatch = useDispatch();
  const { isMobile } = useScreenSize();
  const { isOpen, headerTitle, activeWorkflow } = useSelector(getAIChat);

  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const scrollHeightRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsAnimating(true);
    } else if (shouldRender) {
      setIsAnimating(false);
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, MODAL_ANIMATION_DURATION);
      return () => clearTimeout(timer);
    }
  }, [isOpen, shouldRender]);

  useEffect(() => {
    if (isOpen) analytics.track('AI Coach Opened');
  }, [isOpen]);

  const handleChangeGoal = () => {
    dispatch(toggleAIChat());
    navigate(RoutePath.AICoachGoals, FROM_FINANCIAL_DASHBOARD_STATE);
  };

  const isShiftImpactWorkflow = activeWorkflow?.type === WorkflowTypes.ShiftImpact;
  const isConversationWorkflow = activeWorkflow?.type === WorkflowTypes.Conversation;
  const isPlaidWorkflow = activeWorkflow?.type === WorkflowTypes.Plaid;

  const renderChatWithWorkflow = () => (
    <div>
      <div className={styles.header}>
        {headerTitle ? (
          <>
            <BackLink onClick={() => dispatch(clearActiveWorkflow())} className={styles.backLinkNoMargin} />
            <h5 className={styles.title}>{headerTitle}</h5>
            <div className={styles.spacer} />
          </>
        ) : (
          <div className={styles.defaultHeaderContainer}>
            <div className={styles.titleContainer} onClick={() => dispatch(closeAIChat())}>
              <div className={styles.closeButton}>
                <CloseIcon />
              </div>
              <h5 className={styles.title}>Save and exit</h5>
            </div>
            {/* TODO: This will be a button with this behavior: https://www.figma.com/design/SotHLU5sz6q9CG0d8Z9AcF?node-id=842-5040&m=dev#1530739091 */}
            {/* Waiting on PL-2469 to be implemented */}
            <h5 className={styles.changeGoalButton} onClick={handleChangeGoal}>
              Change goal
            </h5>
          </div>
        )}
      </div>

      <div className={styles.content}>
        <div ref={scrollHeightRef} className={styles.chatHistory}>
          {isPlaidWorkflow && activeWorkflow?.isLoading && WORKFLOWS_COMPONENTS[WorkflowTypes.Plaid]}
          {isShiftImpactWorkflow && WORKFLOWS_COMPONENTS[WorkflowTypes.ShiftImpact]}
          {isConversationWorkflow && <ConversationWorkflow scrollHeightRef={scrollHeightRef} />}
          {!activeWorkflow?.fullScreen && !isShiftImpactWorkflow && !isConversationWorkflow && (
            <Chat scrollHeightRef={scrollHeightRef} />
          )}
        </div>
      </div>

      <div className={styles.disclaimerContainer}>
        <span>{AI_COACH_DISCLAIMER}</span>
      </div>

      <ScrollToBottomButton scrollRef={scrollHeightRef} />
    </div>
  );

  if (!shouldRender) return <></>;

  if (isMobile) {
    return (
      <div className={clsx(styles.mobileOverlay, { [styles.visible]: isAnimating })}>
        <div className={clsx(styles.mobileWrapper, { [styles.open]: isAnimating })}>{renderChatWithWorkflow()}</div>

        <div className={styles.footerContainer}>
          <Footer showDisclaimer={false} />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={clsx(styles.overlay, { [styles.visible]: isAnimating })} />
      <div className={clsx(styles.desktopWrapper, { [styles.open]: isAnimating })}>{renderChatWithWorkflow()}</div>
    </>
  );
};

export default Wrapper;
